import * as Sentry from "@sentry/react";
import { isSentryReplayEnabled } from "config/sentryClient";

const BLOCKED_REPLAY_EXACT_PATHS = new Set([
  "/settings",
  "/settings/testimonial",
  "/admin-panel",
  "/school-dashboard",
  "/classes-manager",
  "/contact",
  "/debug",
  "/quote",
  "/auth/logout",
  "/pricing",
  "/show-pricing",
  "/show",
  "/account/subscription",
  "/referral-program",
  "/thing",
]);

const BLOCKED_REPLAY_PATH_PREFIXES = [
  "/classes-manager/",
  "/share",
  "/admin/",
  "/school/",
  "/purchase-order",
  "/spotify-callback",
  "/canva-callback",
  "/canva-return",
  "/gmail-callback",
  "/auth/logout",
];

const ALLOWED_REPLAY_EXACT_PATHS = new Set([
  "/",
  "/teacher-hub",
  "/schedule-manager",
  "/automations-manager",
  "/automation-timeline",
  "/design-system",
  "/automated-routine-builder",
  "/events-manager",
  "/form",
  "/countdown-form",
  "/countdown-form-v2",
  "/reminder-form",
  "/announcement-form",
  "/music-form",
  "/auto-status",
  "/fullscreen",
  "/export",
  "/print",
  "/faq",
  "/sound-meter-history",
  "/panel",
  "/whats-new",
]);

const ALLOWED_REPLAY_PATH_PREFIXES = [
  "/content-manager/",
  "/calendar/",
];

const DISALLOWED_PRESENT_CLASS_SEGMENTS = new Set([
  "teacher-hub",
  "classes-manager",
  "schedule-manager",
  "automations-manager",
  "automation-timeline",
  "design-system",
  "automated-routine-builder",
  "events-manager",
  "settings",
  "admin-panel",
  "school-dashboard",
  "faq",
  "pricing",
  "contact",
  "debug",
  "share",
  "admin",
  "school",
  "purchase-order",
  "account",
  "auth",
  "blog",
  "tools",
]);

let replayStoppedForSensitiveRoute = false;

export function isSentryReplayConfigured() {
  return isSentryReplayEnabled() && Boolean(process.env.REACT_APP_SENTRY_DSN);
}

export function shouldStopReplayForPath(pathname) {
  if (!pathname) {
    return true;
  }

  if (BLOCKED_REPLAY_EXACT_PATHS.has(pathname)) {
    return true;
  }

  return BLOCKED_REPLAY_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function isPresentClassReplayPath(pathname) {
  if (!pathname || pathname === "/") {
    return pathname === "/";
  }

  const match = pathname.match(/^\/([^/]+)$/);
  if (!match?.[1]) {
    return false;
  }

  return !DISALLOWED_PRESENT_CLASS_SEGMENTS.has(match[1]);
}

export function shouldAllowReplayForPath(pathname) {
  if (!pathname || shouldStopReplayForPath(pathname)) {
    return false;
  }

  if (ALLOWED_REPLAY_EXACT_PATHS.has(pathname)) {
    return true;
  }

  if (ALLOWED_REPLAY_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }

  return isPresentClassReplayPath(pathname);
}

export function syncSentryReplaySession({ pathname }) {
  if (!isSentryReplayConfigured()) {
    return;
  }

  const replay = Sentry.getReplay();
  if (!replay) {
    return;
  }

  if (!shouldAllowReplayForPath(pathname)) {
    replay.stop();
    replayStoppedForSensitiveRoute = true;
    return;
  }

  if (replayStoppedForSensitiveRoute) {
    // Buffer mode only — never replay.start() (that forces full session mode).
    replay.startBuffering();
    replayStoppedForSensitiveRoute = false;
  }
}

export function resetSentryReplaySessionStateForTests() {
  replayStoppedForSensitiveRoute = false;
}
